import loadable from '@loadable/component'
import { matchRoutes, Route, Routes, useLocation, useParams } from 'react-router-dom'
import { useLayoutEffect, useMemo } from 'react'
import CorporateEventLayout from './components/CorporateEventLayout'
import UrlLocation from 'src/lib/url/UrlLocation'
import { useAppDispatch } from 'hooks/reduxHooks'
import { registerPageView } from 'actions/AnalyticsActions'

const CorporateEventLandingPage = loadable(() => import('./pages/CorporateEventLandingPage'))
const CorporateEventFAQPage = loadable(() => import('./pages/CorporateEventFAQPage'))
const CorporateEventOtherActivitiesPage = loadable(() => import('./pages/CorporateEventOtherActivitiesPage'))
const CorporateEventFlightsPage = loadable(() => import('./pages/CorporateEventFlightsPage'))
const CorporateEventAccommodationPage = loadable(() => import('./pages/CorporateEventAccommodationPage'))
const CorporateEventThingsToKnowPage = loadable(() => import('./pages/CorporateEventThingsToKnowPage'))
const CorporateEventRegistrationPage = loadable(() => import('./pages/CorporateEventRegistrationPage'))

const corporateEventRoutes = [
  {
    path: '/:regionCode/corporate-events/:eventId',
    Component: CorporateEventLandingPage,
  },
  {
    path: '/:regionCode/corporate-events/:eventId/faq',
    Component: CorporateEventFAQPage,
  },
  {
    path: '/:regionCode/corporate-events/:eventId/other-activities',
    Component: CorporateEventOtherActivitiesPage,
  },
  {
    path: '/:regionCode/corporate-events/:eventId/flights',
    Component: CorporateEventFlightsPage,
  },
  {
    path: '/:regionCode/corporate-events/:eventId/accommodation',
    Component: CorporateEventAccommodationPage,
  },
  {
    path: '/:regionCode/corporate-events/:eventId/things-to-know',
    Component: CorporateEventThingsToKnowPage,
  },
  {
    path: '/:regionCode/corporate-events/:eventId/register',
    Component: CorporateEventRegistrationPage,
  },
]

interface Props {
  eventId?: string
  regionCode?: string
}

function CorporateEventRoutes(props: Props) {
  const { eventId: paramsEventId, regionCode: paramsRegionCode } = useParams<'eventId' | 'regionCode'>()
  const eventId = props.eventId ?? paramsEventId ?? ''
  const regionCode = props.regionCode ?? paramsRegionCode ?? ''
  const dispatch = useAppDispatch()
  const location = useLocation()
  const localeStrippedLocation = useMemo(() => {
    return {
      ...location,
      pathname: new UrlLocation(location.pathname).withoutLocale().path,
    }
  }, [location])
  const matchedRoute = useMemo(() => {
    return matchRoutes(corporateEventRoutes, localeStrippedLocation)?.at(-1)
  }, [localeStrippedLocation])

  useLayoutEffect(() => {
    if (!matchedRoute) return

    dispatch(registerPageView({
      template: matchedRoute.route.path,
      params: matchedRoute.params,
      path: matchedRoute.pathname,
      search: location.search,
    }))
  }, [dispatch, location.search, matchedRoute])

  return (
    <CorporateEventLayout eventId={eventId} regionCode={regionCode}>
      <Routes location={localeStrippedLocation}>
        <Route index element={<CorporateEventLandingPage />} />
        <Route path="faq" element={<CorporateEventFAQPage />} />
        <Route path="other-activities" element={<CorporateEventOtherActivitiesPage />} />
        <Route path="flights" element={<CorporateEventFlightsPage />} />
        <Route path="accommodation" element={<CorporateEventAccommodationPage />} />
        <Route path="things-to-know" element={<CorporateEventThingsToKnowPage />} />
        <Route path="register" element={<CorporateEventRegistrationPage />} />
      </Routes>
    </CorporateEventLayout>
  )
}

export default CorporateEventRoutes
